import {
  BaseRecord,
  HttpError,
  IResourceComponentsProps,
} from "@refinedev/core";
import { Edit, useForm } from "@refinedev/mantine";
import {
  TextInput,
  Select,
  Box,
  Text,
  Flex,
  Grid,
  Paper,
  Group,
  ThemeIcon,
} from "@mantine/core";
import DateTimePicker from "components/common/DateTimePicker";
import {
  CreateReviewRequest,
  SOURCE_OPTIONS,
} from "entities/review_request";
import {
  IconMail,
  IconUser,
  IconPackage,
  IconCalendar,
  IconWorld,
} from "@tabler/icons";

/* ── Section wrapper ────────────────────────────────────────── */
function FormSection({
  icon,
  title,
  description,
  color,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      p="lg"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
      })}
    >
      <Group spacing="sm" mb={4}>
        <ThemeIcon size={28} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Box>
          <Text size="sm" weight={700}>
            {title}
          </Text>
          <Text size="xs" color="dimmed">
            {description}
          </Text>
        </Box>
      </Group>
      <Box mt="md">{children}</Box>
    </Paper>
  );
}

/* ── Main edit component ────────────────────────────────────── */
export const ReviewRequestEdit: React.FC<IResourceComponentsProps> = () => {
  const { getInputProps, saveButtonProps } = useForm<
    BaseRecord,
    HttpError,
    CreateReviewRequest
  >({
    validateInputOnBlur: true,
    validate: {
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Please enter a valid email address";
        return null;
      },
      source: (value) => {
        if (!value) return "Source is required";
        return null;
      },
    },
  });

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        sx: (theme: any) => ({
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.pink[7]
              : theme.colors.pink[6],
          "&:hover": {
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.colors.pink[6]
                : theme.colors.pink[7],
          },
        }),
      }}
    >
      <Flex direction="column" gap="md">
        {/* ── Customer section ─────────────────────────────── */}
        <FormSection
          icon={<IconUser size={16} />}
          title="Customer Information"
          description="Contact details for the review request recipient"
          color="pink"
        >
          <Grid gutter="md">
            <Grid.Col span={12}>
              <TextInput
                label="Email"
                placeholder="customer@example.com"
                withAsterisk
                icon={<IconMail size={14} />}
                {...getInputProps("email")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Customer Name"
                placeholder="Full name"
                {...getInputProps("customer_name")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="First Name"
                placeholder="First name"
                {...getInputProps("customer_first_name")}
              />
            </Grid.Col>
          </Grid>
        </FormSection>

        {/* ── Source & scheduling section ──────────────────── */}
        <FormSection
          icon={<IconCalendar size={16} />}
          title="Source & Scheduling"
          description="Where this request originates and when to send it"
          color="grape"
        >
          <Grid gutter="md">
            <Grid.Col span={6}>
              <Select
                label="Source"
                placeholder="Select source platform"
                data={SOURCE_OPTIONS}
                withAsterisk
                {...getInputProps("source")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateTimePicker
                label="Scheduled For"
                description="When to send the review request"
                defaultValue={new Date()}
                {...getInputProps("scheduled_for")}
              />
            </Grid.Col>
          </Grid>
        </FormSection>

        {/* ── Order section ────────────────────────────────── */}
        <FormSection
          icon={<IconPackage size={16} />}
          title="Order Details"
          description="Link this request to a specific order"
          color="teal"
        >
          <Grid gutter="md">
            <Grid.Col span={6}>
              <TextInput
                label="Order Number"
                placeholder="e.g., ORD-12345"
                {...getInputProps("order_number")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Remote Order ID"
                placeholder="Remote system ID"
                {...getInputProps("remote_order_id")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Shipping Country"
                placeholder="e.g., JP, US, GB"
                icon={<IconWorld size={14} />}
                {...getInputProps("shipping_country")}
              />
            </Grid.Col>
          </Grid>
        </FormSection>
      </Flex>
    </Edit>
  );
};
